import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import useThemeStore from '../store/themeStore';

const CreateGroup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  
  const [formData, setFormData] = useState({
    name: '',
    budget: '',
    gift_date: '',
    theme: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.gift_date) {
      toast.error(t('error'), { description: 'Name and date are required' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/groups', {
        name: formData.name,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        gift_date: formData.gift_date,
        theme: formData.theme || null,
      });
      
      toast.success(t('success'), { description: 'Group created successfully!' });
      navigate(`/group/${response.data.id}`);
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Failed to create group' });
    }
    setLoading(false);
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-red-50 via-pink-50 to-blue-50'} py-12 px-4`}>
      <div className="container mx-auto max-w-2xl">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('home')}
        </Button>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              {t('createGroupTitle')}
            </h1>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">{t('groupName')}</Label>
                <Input
                  data-testid="group-name-input"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Family Secret Santa 2025"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="budget">{t('budget')}</Label>
                <Input
                  data-testid="budget-input"
                  id="budget"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  placeholder="50.00"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="gift_date">{t('giftDate')}</Label>
                <Input
                  data-testid="gift-date-input"
                  id="gift_date"
                  type="date"
                  value={formData.gift_date}
                  onChange={(e) => setFormData({ ...formData, gift_date: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="theme">{t('theme')}</Label>
                <Textarea
                  data-testid="theme-input"
                  id="theme"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  placeholder="Christmas, Winter Wonderland, etc."
                  rows={3}
                />
              </div>
              
              <div className="flex gap-4">
                <Button
                  data-testid="cancel-button"
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  {t('cancel')}
                </Button>
                <Button
                  data-testid="create-button"
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-rose-600 hover:bg-rose-700"
                >
                  {loading ? t('loading') : t('create')}
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateGroup;